import { QAReviewList } from '@/components/email-qa/qa-review-list'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useState } from 'react'
import {
  ClipboardCheck,
  BarChart3,
  Settings,
  Info,
  ArrowRight,
  Users
} from 'lucide-react'

export function EmailQADashboard() {
  const [currentReviewer, setCurrentReviewer] = useState<string>(() => {
    // Load saved reviewer from localStorage (consistent across QA tools)
    return localStorage.getItem('qa_selected_reviewer') || 'Bernhard'
  })

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <ClipboardCheck className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0" />
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">
                  E-Mail QA Dashboard
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground">
                  Qualitätsbewertung und Verbesserung KI-generierter E-Mails
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              {/* Reviewer Selection */}
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Reviewer:</span>
                <Select
                  value={currentReviewer}
                  onValueChange={(value) => {
                    setCurrentReviewer(value)
                    localStorage.setItem('qa_selected_reviewer', value)
                  }}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bernhard">Bernhard</SelectItem>
                    <SelectItem value="Thomas">Thomas</SelectItem>
                    <SelectItem value="Christoph">Christoph</SelectItem>
                    <SelectItem value="Stefan">Stefan</SelectItem>
                    <SelectItem value="Mathias">Mathias</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                className="text-sm sm:text-base"
                disabled
              >
                <Settings className="mr-2 h-4 w-4" />
                Groq Integration
                <Badge variant="secondary" className="ml-2">Soon</Badge>
              </Button>
            </div>
          </div>

          {/* Info Banner */}
          <Card className="mb-6 border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Info className="h-5 w-5" />
                Über das QA Dashboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <p>
                  Dieses Dashboard ermöglicht die systematische Bewertung und Verbesserung
                  der KI-generierten E-Mail-Antworten. Jede E-Mail durchläuft eine Agent-Pipeline:
                </p>
                <div className="flex items-center gap-2 text-xs bg-white dark:bg-gray-900 p-2 rounded border">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">📧 Original</span>
                  <ArrowRight className="h-3 w-3" />
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">🔍 Extractor</span>
                  <ArrowRight className="h-3 w-3" />
                  <span className="px-2 py-1 bg-teal-100 text-teal-800 rounded">🎨 Styler</span>
                  <ArrowRight className="h-3 w-3" />
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded">🔧 Refiner</span>
                  <ArrowRight className="h-3 w-3" />
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded">📄 Final</span>
                </div>
                <p>
                  <strong>Ziel:</strong> Durch systematische Bewertungen (1-10) und Feedback
                  können Muster erkannt und die Prompt-Pipeline kontinuierlich verbessert werden.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main QA Review List */}
        <QAReviewList currentReviewer={currentReviewer} />
      </div>
    </div>
  )
}